// src/pages/ChangeCredentials.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_API_URL || "";

export default function ChangeCredentials() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [section, setSection] = useState("password");

  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwErr, setPwErr] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const [emForm, setEmForm] = useState({ password: "", newEmail: "", confirmEmail: "" });
  const [emErr, setEmErr] = useState("");
  const [emSuccess, setEmSuccess] = useState("");
  const [emSaving, setEmSaving] = useState(false);

  const inputStyle = {
    border: "1px solid #d0dcea", borderRadius: 6, padding: "8px 10px",
    fontSize: 14, width: "100%", boxSizing: "border-box",
  };
  const labelStyle = {
    display: "block", fontSize: 13, fontWeight: 500, color: "#3a5068", marginBottom: 4,
  };
  const cardStyle = {
    background: "white", borderRadius: 12, border: "1px solid #d0dcea", padding: "24px",
    display: "flex", flexDirection: "column", gap: 16,
  };

  async function handlePasswordSubmit(e) {
    e.preventDefault();
    setPwErr("");
    setPwSuccess("");
    if (pwForm.next !== pwForm.confirm) return setPwErr("New passwords do not match.");
    if (pwForm.next.length < 8) return setPwErr("New password must be at least 8 characters.");
    setPwSaving(true);
    try {
      const res = await fetch(`${API}/india/users/me/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to update password");
      setPwSuccess("Password updated successfully.");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwErr(err.message);
    } finally {
      setPwSaving(false);
    }
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    setEmErr("");
    setEmSuccess("");
    if (emForm.newEmail !== emForm.confirmEmail) return setEmErr("Email addresses do not match.");
    setEmSaving(true);
    try {
      const res = await fetch(`${API}/india/users/me/change-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: emForm.password, new_email: emForm.newEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to update email");
      setEmSuccess("Email updated successfully. Please log in again with your new email.");
      setEmForm({ password: "", newEmail: "", confirmEmail: "" });
    } catch (err) {
      setEmErr(err.message);
    } finally {
      setEmSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 512, margin: "0 auto", display: "flex", flexDirection: "column", gap: 24 }}>
      <button
        onClick={() => navigate("/profile")}
        style={{ fontSize: 13, color: "#6b8cae", background: "none", border: "none", cursor: "pointer", padding: 0, alignSelf: "flex-start" }}
      >
        ← Back to profile
      </button>

      <h1 style={{ fontSize: 22, fontWeight: 600, color: "#1e3a5f", margin: 0 }}>Account security</h1>

      <div style={{ display: "flex", gap: 8 }}>
        {["password", "email"].map(s => (
          <button key={s} onClick={() => setSection(s)} style={{
            padding: "6px 12px", borderRadius: 6, fontSize: 13, cursor: "pointer",
            border: "1px solid #d0dcea",
            background: section === s ? "#1e3a5f" : "white",
            color: section === s ? "white" : "#1e3a5f",
          }}>
            {s === "password" ? "Change password" : "Change email"}
          </button>
        ))}
      </div>

      {section === "password" && (
        <form onSubmit={handlePasswordSubmit} style={cardStyle}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: "#1e3a5f", margin: 0 }}>Change password</h2>
          {pwErr && <div style={{ fontSize: 13, color: "#c0392b", background: "#fdecea", border: "1px solid #f5c6c2", borderRadius: 6, padding: "8px 12px" }}>{pwErr}</div>}
          {pwSuccess && <div style={{ fontSize: 13, color: "#2e7d32", background: "#e6f4ea", border: "1px solid #c3e6cb", borderRadius: 6, padding: "8px 12px" }}>{pwSuccess}</div>}
          <div>
            <label style={labelStyle}>Current password</label>
            <input type="password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} style={inputStyle} required />
          </div>
          <div>
            <label style={labelStyle}>New password</label>
            <input type="password" value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} style={inputStyle} required />
          </div>
          <div>
            <label style={labelStyle}>Confirm new password</label>
            <input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} style={inputStyle} required />
          </div>
          <button type="submit" disabled={pwSaving} style={{
            padding: "8px 16px", background: "#1e3a5f", color: "white",
            border: "none", borderRadius: 8, fontSize: 14,
            opacity: pwSaving ? 0.6 : 1, cursor: pwSaving ? "not-allowed" : "pointer",
          }}>
            {pwSaving ? "Updating…" : "Update password"}
          </button>
        </form>
      )}

      {section === "email" && (
        <form onSubmit={handleEmailSubmit} style={cardStyle}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: "#1e3a5f", margin: 0 }}>Change email address</h2>
          <div style={{ fontSize: 13, color: "#6b8cae" }}>Current email: <strong>{user?.email || "—"}</strong></div>
          {emErr && <div style={{ fontSize: 13, color: "#c0392b", background: "#fdecea", border: "1px solid #f5c6c2", borderRadius: 6, padding: "8px 12px" }}>{emErr}</div>}
          {emSuccess && <div style={{ fontSize: 13, color: "#2e7d32", background: "#e6f4ea", border: "1px solid #c3e6cb", borderRadius: 6, padding: "8px 12px" }}>{emSuccess}</div>}
          <div>
            <label style={labelStyle}>Current password</label>
            <input type="password" value={emForm.password} onChange={e => setEmForm(f => ({ ...f, password: e.target.value }))} style={inputStyle} required />
          </div>
          <div>
            <label style={labelStyle}>New email address</label>
            <input type="email" value={emForm.newEmail} onChange={e => setEmForm(f => ({ ...f, newEmail: e.target.value }))} style={inputStyle} required />
          </div>
          <div>
            <label style={labelStyle}>Confirm new email address</label>
            <input type="email" value={emForm.confirmEmail} onChange={e => setEmForm(f => ({ ...f, confirmEmail: e.target.value }))} style={inputStyle} required />
          </div>
          <button type="submit" disabled={emSaving} style={{
            padding: "8px 16px", background: "#1e3a5f", color: "white",
            border: "none", borderRadius: 8, fontSize: 14,
            opacity: emSaving ? 0.6 : 1, cursor: emSaving ? "not-allowed" : "pointer",
          }}>
            {emSaving ? "Updating…" : "Update email"}
          </button>
        </form>
      )}
    </div>
  );
}